// src/components/admin/RenewalDashboard.jsx
import { useEffect, useState } from "react";
import { supabase } from "../../utils/supabaseClient";
import {
  Box,
  Heading,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Select,
} from "@chakra-ui/react";

/**
 * RenewalDashboard
 * ADMIN‑ONLY VIEW
 * Shows:
 * - all sectors
 * - flyer season/year
 * - sold/available/branding/cancelled counts
 * - renewal risk
 * - auto‑renew predictions
 */
export default function RenewalDashboard() {
  const [region, setRegion] = useState("PR");
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("flyers")
        .select(`
          id,
          postcode_sector,
          season,
          year,
          status,
          flyer_slots (
            status,
            cancelled,
            reserved_for
          )
        `)
        .eq("region", region)
        .order("postcode_sector", { ascending: true });

      if (!error) setFlyers(data || []);

      setLoading(false);
    };

    load();
  }, [region]);

  const computeStats = (flyer) => {
    const slots = flyer.flyer_slots || [];

    return {
      sold: slots.filter((s) => s.status === "sold").length,
      branding: slots.filter((s) => s.status === "branding").length,
      available: slots.filter((s) => s.status === "available").length,
      cancelled: slots.filter((s) => s.cancelled).length,
      reserved: slots.filter((s) => s.reserved_for).length,
    };
  };

  return (
    <Box p={6} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm">
      <Heading size="md" mb={4}>
        🔄 Renewal Dashboard
      </Heading>

      {/* Region selector */}
      <Flex gap={4} mb={4}>
        <Select
          width="200px"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
        >
          <option value="PR">PR</option>
          <option value="AB">AB</option>
          <option value="BD">BD</option>
          <option value="NW">NW</option>
          <option value="PO">PO</option>
          <option value="SA">SA</option>
          <option value="RM">RM</option>
          <option value="UK">UK</option>
        </Select>
      </Flex>

      {loading && (
        <Flex align="center" justify="center" p={6}>
          <Spinner size="lg" />
          <Text ml={3}>Loading renewal data…</Text>
        </Flex>
      )}

      {!loading && (
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Sector</Th>
              <Th>Season</Th>
              <Th>Sold</Th>
              <Th>Branding</Th>
              <Th>Available</Th>
              <Th>Cancelled</Th>
              <Th>Reserved</Th>
              <Th>Renewal Risk</Th>
            </Tr>
          </Thead>

          <Tbody>
            {flyers.map((f) => {
              const stats = computeStats(f);

              const risk =
                stats.cancelled >= 10
                  ? "High"
                  : stats.cancelled >= 5
                  ? "Medium"
                  : "Low";

              const riskColor =
                risk === "High"
                  ? "red"
                  : risk === "Medium"
                  ? "orange"
                  : "green";

              return (
                <Tr key={f.id}>
                  <Td>{f.postcode_sector}</Td>
                  <Td>
                    {f.season} {f.year}
                  </Td>
                  <Td>
                    <Badge colorScheme="red">{stats.sold}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="purple">{stats.branding}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="green">{stats.available}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="orange">{stats.cancelled}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="yellow">{stats.reserved}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={riskColor}>{risk}</Badge>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
